import { BRAND } from "@bigpp/config/brand";
import { FILE_INPUT_ACCEPT, resolveMime } from "@bigpp/trpc/supportedFiles";
import { Paperclip, Send, Square, X } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { Badge } from "@bigpp/ui/components/badge";
import { Button } from "@bigpp/ui/components/button";
import { Textarea } from "@bigpp/ui/components/textarea";
import { Tooltip } from "@bigpp/ui/components/tooltip";
import { cn } from "@bigpp/ui/lib/utils";

import type { AttachmentInput } from "../agent/useAgentRun.ts";
import { SpeechInput } from "./SpeechInput.tsx";

type PendingAttachment = AttachmentInput;

export type AttachFilesFn = (files: FileList | File[]) => void;

const newId = (): string => globalThis.crypto.randomUUID();

const intakeFiles = (
  files: FileList | File[],
): { accepted: PendingAttachment[]; rejected: string[] } => {
  const accepted: PendingAttachment[] = [];
  const rejected: string[] = [];
  for (const file of Array.from(files)) {
    const mime = resolveMime(file.name, file.type);
    if (!mime) {
      rejected.push(file.name);
      continue;
    }
    accepted.push({ id: newId(), file, filename: file.name, mediaType: mime });
  }
  return { accepted, rejected };
};

type Props = {
  running: boolean;
  onSend: (text: string, attachments: PendingAttachment[]) => void;
  onStop: () => void;
  leftSlot?: React.ReactNode;
  attachFilesRef?: React.MutableRefObject<AttachFilesFn | null>;
};

export function Composer({ running, onSend, onStop, leftSlot, attachFilesRef }: Props) {
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const [shake, setShake] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Auto-resize the textarea to fit its content, capped at 30% of the addin
  // viewport. Beyond that, the textarea scrolls internally. Recomputes on
  // text change and on window resize.
  useLayoutEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;

    const resize = () => {
      const cap = Math.max(40, Math.floor(window.innerHeight * 0.3));
      ta.style.height = "0px";
      const next = Math.min(ta.scrollHeight, cap);
      ta.style.height = `${next.toString()}px`;
      ta.style.overflowY = ta.scrollHeight > cap ? "auto" : "hidden";
    };
    resize();

    window.addEventListener("resize", resize);
    return () => {
      window.removeEventListener("resize", resize);
    };
  }, [text]);

  const addFiles = useCallback<AttachFilesFn>((files) => {
    const { accepted, rejected } = intakeFiles(files);
    if (accepted.length > 0) {
      setAttachments((prev) => [...prev, ...accepted]);
    }
    if (rejected.length > 0) {
      toast.warning(
        `Can't attach ${rejected.join(", ")} — supported types: PDF, PPTX, DOCX, CSV, TXT, MD, PNG, JPG.`,
      );
    }
  }, []);

  useEffect(() => {
    if (!attachFilesRef) return;
    attachFilesRef.current = addFiles;
    return () => {
      attachFilesRef.current = null;
    };
  }, [attachFilesRef, addFiles]);

  const submit = () => {
    if (running) return;
    const trimmed = text.trim();
    if (!trimmed && attachments.length === 0) {
      setShake(true);
      window.setTimeout(() => {
        setShake(false);
      }, 400);
      return;
    }
    onSend(trimmed, attachments);
    setText("");
    setAttachments([]);
  };

  const canSend = text.trim().length > 0 || attachments.length > 0;

  return (
    <div className="flex flex-col gap-2">
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {attachments.map((a) => (
            <Badge key={a.id} variant="default" className="pl-2 pr-1 gap-1">
              <Paperclip className="size-3" />
              <span className="truncate max-w-[160px]">{a.filename}</span>
              <button
                type="button"
                onClick={() => {
                  setAttachments((prev) => prev.filter((x) => x.id !== a.id));
                }}
                aria-label={`Remove ${a.filename}`}
                className="ml-0.5 rounded-sm p-0.5 hover:bg-[var(--color-border)]"
              >
                <X className="size-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      <div
        className={cn(
          "flex flex-col gap-2 rounded-lg border border-[var(--color-input)] bg-[var(--color-card)] p-2 transition-shadow",
          "focus-within:ring-2 focus-within:ring-[var(--color-ring)] focus-within:ring-offset-1",
          shake && "animate-shake",
        )}
      >
        <Textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder={`Ask ${BRAND} to help you with your deck.`}
          rows={1}
          disabled={running}
          className="border-0 shadow-none p-0 min-h-[40px] focus-visible:ring-0 focus-visible:ring-offset-0"
        />
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1">
            <Tooltip label="Attach files">
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={running}
                aria-label="Attach files"
                data-tour="attach"
              >
                <Paperclip />
              </Button>
            </Tooltip>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={FILE_INPUT_ACCEPT}
              onChange={(e) => {
                if (e.target.files && e.target.files.length > 0) addFiles(e.target.files);
                e.target.value = "";
              }}
              className="hidden"
            />
            <span data-tour="dictate">
              <SpeechInput
                disabled={running}
                onTranscript={(transcript) => {
                  setText((prev) => {
                    if (prev.length === 0) return transcript;
                    return prev.endsWith(" ") ? `${prev}${transcript}` : `${prev} ${transcript}`;
                  });
                }}
              />
            </span>
            {leftSlot}
          </div>
          {running ? (
            <Button type="button" variant="destructive" size="sm" onClick={onStop}>
              <Square className="size-3" />
              Stop
            </Button>
          ) : (
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={submit}
              disabled={!canSend}
            >
              <Send className="size-3" />
              Send
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
